/*
  OA — Image placeholder + swipe helper.

  Product photos are lazy-loaded, so before an image loads its slot used to
  show a blank white frame. The CSS (component-card.css for the mobile card
  swipe, section-main-product.css for the product-page gallery) now paints a
  #f4f4f4 tile with a soft "opening act." watermark behind every image, so the
  gap is branded instead of white.

  This script adds progressive enhancements on top of that CSS. If it does not
  run, images simply show normally and the swipe still scrolls — nothing breaks.

    1. Fade-in: each image fades in once it finishes loading.
    2. Preload: neighbouring images are warmed ahead of time.
    3. Endless loop (card swipe only): the in-card image swipe wraps around —
       swiping past the last image returns to the first, and swiping back from
       the first shows the last, like a continuous carousel. Done by cloning
       the first/last slides at each end and jumping invisibly to the real
       slide once a clone is reached.

  Two surfaces:
    - .oa-swipe            in-card swipe track (collection grids), <990px (mobile + tablet).
    - media-gallery        the product-page gallery, all widths.
*/
(function () {
  'use strict';

  // Active on mobile AND the 3-col tablet range (< 990px) — everywhere the
  // desktop hover secondary-image (min-width: 990px) does NOT apply. Kept in
  // sync with the .oa-swipe media query in component-card.css.
  var MOBILE = window.matchMedia('(max-width: 989px)');

  function warm(img) {
    if (!img || img.dataset.oaWarm) return;
    img.dataset.oaWarm = '1';
    var pre = new Image();
    if (img.sizes) pre.sizes = img.sizes;
    if (img.srcset) pre.srcset = img.srcset;
    var src = img.currentSrc || img.src;
    if (src) pre.src = src;
  }

  function markLoaded(img) {
    if (!img) return;
    var done = function () {
      img.classList.add('oa-loaded');
      // Fade the placeholder watermark out once the real image is painted, so
      // it can't linger in the letterbox margins under an object-fit:contain
      // image. The image's own parent carries the ::before watermark.
      if (img.parentElement) img.parentElement.classList.add('oa-shown');
    };
    if (img.complete && img.naturalWidth > 0) {
      done();
    } else {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
  }

  function list(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  /* ---- Mobile in-card swipe track (endless loop) ---- */

  function setupTrack(track) {
    if (track.dataset.oaPh) return;
    track.dataset.oaPh = '1';
    track.classList.add('oa-swipe--fade');

    var real = list(track, '.oa-swipe__slide');
    // In the "Bag It Up" flip card (featured-split, .oa-split) the back grid is
    // rendered with a CSS `zoom`, which makes the track width fractional. The
    // endless-loop LEADING clone (a copy of the LAST image placed before the
    // first, for the backward wrap) then peeks a ~1px sliver on the left at rest.
    // Disable the wrap inside .oa-split so the first slide sits flush at
    // scrollLeft 0 (no clone in front) — the swipe still scrolls, it just doesn't
    // wrap around. Normal collection grids are unaffected and keep the loop.
    var noWrap = !!(track.closest && track.closest('.oa-split'));
    var loop = real.length > 1 && !noWrap;

    // Clone the last slide before the first, and the first slide after the
    // last, so there is always a "next" frame to scroll into at either end.
    if (loop) {
      var lead = real[real.length - 1].cloneNode(true); // clone of last -> front
      var trail = real[0].cloneNode(true);               // clone of first -> back
      lead.dataset.oaClone = '1';
      trail.dataset.oaClone = '1';
      lead.setAttribute('aria-hidden', 'true');
      trail.setAttribute('aria-hidden', 'true');
      track.insertBefore(lead, real[0]);
      track.appendChild(trail);
    }

    var slides = list(track, '.oa-swipe__slide');
    var imgs = slides.map(function (s) { return s.querySelector('img'); });
    imgs.forEach(markLoaded);

    if (!loop) {
      warm(imgs[0]);
      return;
    }

    // Slide positions after cloning: 0 = clone-of-last, 1..n = real, n+1 = clone-of-first.
    var n = real.length;
    var width = function () { return Math.max(1, track.clientWidth); };
    var current = 1;
    var lastW = 0;

    function jumpTo(px) {
      var prev = track.style.scrollBehavior;
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = px;
      track.style.scrollBehavior = prev || '';
    }

    // Start on the first real slide (position 1), not the leading clone.
    function start() {
      if (track.clientWidth === 0) { requestAnimationFrame(start); return; }
      lastW = width();
      jumpTo(lastW);
    }
    start();

    function settle() {
      var w = width();
      var i = Math.round(track.scrollLeft / w);
      if (i <= 0) {
        jumpTo(n * w);   // leading clone (of last) -> real last
        current = n;
      } else if (i >= n + 1) {
        jumpTo(w);       // trailing clone (of first) -> real first
        current = 1;
      } else {
        current = i;
      }
    }

    var settleTimer, ticking = false;
    track.addEventListener('scroll', function () {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var i = Math.round(track.scrollLeft / width());
          warm(imgs[i]);
          warm(imgs[i + 1]);
          warm(imgs[i - 1]);
        });
      }
      clearTimeout(settleTimer);
      settleTimer = setTimeout(settle, 120);
    }, { passive: true });

    // Reposition to the same image only when the WIDTH changes (orientation),
    // not on the vertical resizes iOS fires when the URL bar shows/hides.
    var rTimer;
    window.addEventListener('resize', function () {
      clearTimeout(rTimer);
      rTimer = setTimeout(function () {
        var w = width();
        if (w === lastW) return;
        lastW = w;
        jumpTo(Math.min(n, Math.max(1, current)) * w);
      }, 150);
    });

    // Warm the first images and the wrap targets (real first + real last) so
    // wrapping around never lands on a placeholder.
    warm(imgs[1]);
    warm(imgs[2]);
    warm(imgs[n]);
  }

  /* ---- Product-page gallery ---- */

  function galleryImg(slide) {
    return slide
      ? slide.querySelector('.product-media-container.media-type-image .product__media img')
      : null;
  }

  function setupGallery(gallery) {
    if (gallery.dataset.oaPh) return;
    gallery.dataset.oaPh = '1';
    gallery.classList.add('oa-gfade');

    list(gallery, '.product-media-container.media-type-image .product__media img').forEach(markLoaded);

    var slides = list(gallery, '.product__media-list > .product__media-item');
    var slideByMedia = {};
    var active = 0;
    slides.forEach(function (s, i) {
      slideByMedia[s.getAttribute('data-media-id')] = s;
      if (s.classList.contains('is-active')) active = i;
    });

    // Warm the visible slide and its immediate neighbours up front.
    warm(galleryImg(slides[active]));
    warm(galleryImg(slides[active + 1]));
    warm(galleryImg(slides[active - 1]));

    // Desktop: warm a main image the moment its thumbnail is hovered/focused,
    // so the swap on click is instant.
    list(gallery, '.thumbnail-list__item[data-target]').forEach(function (item) {
      var go = function () { warm(galleryImg(slideByMedia[item.getAttribute('data-target')])); };
      item.addEventListener('pointerenter', go, { once: true });
      item.addEventListener('focusin', go, { once: true });
    });

    // Mobile: warm neighbours as the main slider is swiped.
    var track = gallery.querySelector('.product__media-list');
    if (track) {
      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          var i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          warm(galleryImg(slides[i]));
          warm(galleryImg(slides[i + 1]));
          warm(galleryImg(slides[i - 1]));
        });
      }, { passive: true });
    }
  }

  /* ---- Scanning ---- */

  function scanCards() {
    if (!MOBILE.matches) return;
    var tracks = list(document, '.oa-swipe');
    if (!tracks.length) return;
    if (!('IntersectionObserver' in window)) {
      tracks.forEach(setupTrack);
      return;
    }
    // display:none tracks (desktop, carousels) never intersect, so they are
    // skipped for free; the rest are set up as they near the viewport.
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          setupTrack(e.target);
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '300px 0px' });
    tracks.forEach(function (t) { if (!t.dataset.oaPh) io.observe(t); });
  }

  function scanGalleries() {
    list(document, 'media-gallery').forEach(setupGallery);
  }

  function scan() {
    scanCards();
    scanGalleries();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }
  // Re-scan when sections re-render (filtering, theme editor) or on breakpoint change.
  document.addEventListener('shopify:section:load', scan);
  if (MOBILE.addEventListener) {
    MOBILE.addEventListener('change', scan);
  } else if (MOBILE.addListener) {
    MOBILE.addListener(scan);
  }
})();
